import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Shield, User, Eye, Trash2, UserPlus, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { useWorkspaceStore } from '../stores/workspace.store';
import { useAuthStore } from '../stores/auth.store';
import { workspaceApi } from '../services/workspace.service';

// ── Types ─────────────────────────────────────────────────────────────────────

interface WorkspaceMember {
  id: string;
  userId: string;
  role: string;
  joinedAt: string;
  user: {
    id: string;
    email: string;
    name: string | null;
    avatarUrl: string | null;
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<string, string> = {
  viewer: 'Leitor',
  user: 'Usuário',
  admin: 'Admin',
};

const ROLE_COLORS: Record<string, string> = {
  viewer: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  user: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
};

const ROLE_ICONS: Record<string, React.ReactNode> = {
  viewer: <Eye className="h-3 w-3" />,
  user: <User className="h-3 w-3" />,
  admin: <Shield className="h-3 w-3" />,
};

function RoleBadge({ role }: { role: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        ROLE_COLORS[role] ?? ROLE_COLORS.viewer
      }`}
    >
      {ROLE_ICONS[role]}
      {ROLE_LABELS[role] ?? role}
    </span>
  );
}

function MemberAvatar({ user }: { user: WorkspaceMember['user'] }) {
  const initials = user.name
    ? user.name.charAt(0).toUpperCase()
    : user.email.charAt(0).toUpperCase();
  return (
    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">
      {user.avatarUrl ? (
        <img
          src={user.avatarUrl}
          alt={user.name ?? user.email}
          className="w-9 h-9 rounded-full object-cover"
        />
      ) : (
        initials
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function WorkspaceSettingsPage() {
  const navigate = useNavigate();
  const { currentWorkspace, setCurrentWorkspace } = useWorkspaceStore();
  const { user: currentUser } = useAuthStore();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const isAdmin = currentWorkspace?.role === 'admin';

  // ─ Geral state
  const [name, setName] = useState(currentWorkspace?.name ?? '');
  useEffect(() => {
    if (currentWorkspace) setName(currentWorkspace.name);
  }, [currentWorkspace]);

  // ─ Members state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');

  // ─ Queries & mutations
  const { data: members = [], isLoading: membersLoading } = useQuery<WorkspaceMember[]>({
    queryKey: ['workspace-members', currentWorkspace?.id],
    queryFn: () => workspaceApi.getMembers(currentWorkspace!.id),
    enabled: !!currentWorkspace,
  });

  const updateNameMutation = useMutation({
    mutationFn: (newName: string) =>
      workspaceApi.update(currentWorkspace!.id, { name: newName }),
    onSuccess: (updated) => {
      setCurrentWorkspace({ ...currentWorkspace!, ...updated });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast({ title: 'Nome atualizado!' });
    },
    onError: (err: any) => {
      toast({ title: 'Erro ao salvar', description: err.message, variant: 'destructive' });
    },
  });

  const addMutation = useMutation({
    mutationFn: (data: { email: string; role: string }) =>
      workspaceApi.addMember(currentWorkspace!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-members', currentWorkspace?.id] });
      setInviteEmail('');
      setInviteRole('viewer');
      toast({ title: 'Membro adicionado com sucesso!' });
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message;
      toast({
        title: 'Erro ao adicionar membro',
        description: Array.isArray(msg) ? msg.join(', ') : (msg ?? err.message),
        variant: 'destructive',
      });
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: string }) =>
      workspaceApi.updateMember(currentWorkspace!.id, memberId, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-members', currentWorkspace?.id] });
      toast({ title: 'Cargo atualizado!' });
    },
    onError: (err: any) => {
      toast({
        title: 'Erro ao atualizar cargo',
        description: err.response?.data?.message ?? err.message,
        variant: 'destructive',
      });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (memberId: string) =>
      workspaceApi.removeMember(currentWorkspace!.id, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-members', currentWorkspace?.id] });
      toast({ title: 'Membro removido.' });
    },
    onError: (err: any) => {
      toast({
        title: 'Erro ao remover membro',
        description: err.response?.data?.message ?? err.message,
        variant: 'destructive',
      });
    },
  });

  const handleSaveName = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) updateNameMutation.mutate(name.trim());
  };

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (inviteEmail.trim()) addMutation.mutate({ email: inviteEmail.trim(), role: inviteRole });
  };

  return (
    <div className="max-w-2xl mx-auto py-8 px-6 space-y-10">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} title="Voltar">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Configurações do Workspace</h1>
          <p className="text-sm text-muted-foreground">{currentWorkspace?.name}</p>
        </div>
      </div>

      {/* ── Geral ─────────────────────────────────────────────────────────── */}
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Geral</h2>
          <p className="text-sm text-muted-foreground">Informações básicas do workspace</p>
        </div>
        <div className="border rounded-lg p-5">
          <form onSubmit={handleSaveName} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ws-name">Nome</Label>
              <div className="flex gap-3">
                <Input
                  id="ws-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nome do workspace"
                  className="flex-1"
                  disabled={!isAdmin}
                />
                {isAdmin && (
                  <Button type="submit" disabled={!name.trim() || updateNameMutation.isPending}>
                    {updateNameMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Salvar'
                    )}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* ── Membros ───────────────────────────────────────────────────────── */}
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Membros</h2>
          <p className="text-sm text-muted-foreground">
            Gerencie quem tem acesso a este workspace
          </p>
        </div>

        {/* Invite form — admin only */}
        {isAdmin && (
          <div className="border rounded-lg p-5 space-y-4">
            <h3 className="text-sm font-semibold">Adicionar membro</h3>
            <form onSubmit={handleInvite} className="flex gap-2">
              <Input
                placeholder="email@exemplo.com"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="flex-1"
                required
              />
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Leitor</SelectItem>
                  <SelectItem value="user">Usuário</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <Button
                type="submit"
                size="icon"
                disabled={!inviteEmail.trim() || addMutation.isPending}
                title="Adicionar"
              >
                {addMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <UserPlus className="h-4 w-4" />
                )}
              </Button>
            </form>
            <div className="text-xs text-muted-foreground space-y-1">
              <p><span className="font-medium">Leitor</span> — somente visualização</p>
              <p>
                <span className="font-medium">Usuário</span> — edita dados (tabelas, kanbans),
                mas não a estrutura das páginas
              </p>
              <p><span className="font-medium">Admin</span> — acesso completo</p>
            </div>
          </div>
        )}

        {/* Members list */}
        <div className="border rounded-lg overflow-hidden">
          {membersLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {members.map((member) => {
                const isSelf = member.user.id === currentUser?.id;
                return (
                  <li key={member.id} className="flex items-center gap-3 px-4 py-3">
                    <MemberAvatar user={member.user} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {member.user.name ?? member.user.email}
                        {isSelf && (
                          <span className="ml-1 text-xs text-muted-foreground">(você)</span>
                        )}
                      </p>
                      {member.user.name && (
                        <p className="text-xs text-muted-foreground truncate">
                          {member.user.email}
                        </p>
                      )}
                    </div>

                    {isAdmin && !isSelf ? (
                      <Select
                        value={member.role}
                        onValueChange={(role) =>
                          updateRoleMutation.mutate({ memberId: member.id, role })
                        }
                        disabled={updateRoleMutation.isPending}
                      >
                        <SelectTrigger className="h-8 w-28 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="viewer">Leitor</SelectItem>
                          <SelectItem value="user">Usuário</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <RoleBadge role={member.role} />
                    )}

                    {isAdmin && !isSelf && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => removeMutation.mutate(member.id)}
                        disabled={removeMutation.isPending}
                        title="Remover membro"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
